import { Component, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Subject } from 'rxjs';
import { ScannerInputComponent } from '@/shared/scanner-input.component';
import { DialogBaseComponent } from '@/shared/dialog-base.component';
import { ShipFrequencyDialogComponent } from './ship-frequency-dialog.component';

@Component({
  selector: 'uni-process-manager',
  standalone: false,
  templateUrl: './process-manager.component.html',
})
export class ProcessManagerComponent {
  @ViewChild('shipPrepScanner') scannerInput: ScannerInputComponent<unknown>;
  @ViewChild('joPullApplyScannerInput') joPullApplyScannerInput: ScannerInputComponent<unknown>;

  pngDialogService: DialogService;

  constructor(dialogService: DialogService) {
    this.pngDialogService = dialogService;
  }

  isSidebarCollapsed = false;

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;

    // If expanding and a scanner is visible, refocus it after animation completes
    if (!this.isSidebarCollapsed) {
      if (this.showScanIntoShipPrepButton || this.showAddonBinScanner || this.showScanStationScanner) {
        setTimeout(() => this.focusScanner(), 850);
      } else if (this.showJoPullApplyScanner) {
        setTimeout(() => this.focusJoPullApplyScanner(), 850);
      }
    }
  }

  // Context-sensitive button and scanner for Jo Ship Prep page
  scanIntoShipPrepClicked$ = new Subject<void>();
  showScanIntoShipPrepButton = false;

  // Context-sensitive scanner for Scan Station page
  showScanStationScanner = false;

  // Context-sensitive scanner for Addon Bin Process page
  showAddonBinScanner = false;
  addonBinScannerLabel = 'Scan Item Detail...';

  // Scanner properties - controlled by child page (shared between Ship Prep and Addon Bin)
  scannerDisabled = false;
  scannerSearchFn: (value: string) => Promise<unknown | null> = async () => null;
  scannerResult$ = new Subject<unknown>();
  scannerError$ = new Subject<string>();

  onScannerResult(result: unknown) {
    this.scannerResult$.next(result);
  }

  onScannerError(error: string) {
    this.scannerError$.next(error);
  }

  focusScanner() {
    this.scannerInput?.focus();
  }

  // Context-sensitive buttons and scanner for Jo Pull Apply page
  confirmSelectionClicked$ = new Subject<void>();
  shouldPrintBarcodesApplyChanged$ = new Subject<boolean>();
  showJoPullApplyButtons = false;
  canConfirmSelection = false;
  shouldPrintBarcodesApply = true;

  // Jo Pull Apply scanner
  showJoPullApplyScanner = false;
  joPullApplyScannerDisabled = false;
  joPullApplyScannerSearchFn: (value: string) => Promise<unknown | null> = async () => null;
  joPullApplyScannerResult$ = new Subject<unknown>();
  joPullApplyScannerError$ = new Subject<string>();

  onJoPullApplyScannerResult(result: unknown) {
    this.joPullApplyScannerResult$.next(result);
  }

  onJoPullApplyScannerError(error: string) {
    this.joPullApplyScannerError$.next(error);
  }

  focusJoPullApplyScanner() {
    // Focus the pull-apply scanner specifically (second scanner instance)
    if (this.joPullApplyScannerInput) {
      this.joPullApplyScannerInput.focus();
    }
  }

  // Context-sensitive button for Ship Frequencies (visible on Ship Prep, Staging/Vouchering, Shipping/Invoicing)
  showShipFrequenciesButton = false;

  async editShipFrequencies() {
    await DialogBaseComponent.open(ShipFrequencyDialogComponent, this);
  }

  scanIntoShipPrep() {
    this.scanIntoShipPrepClicked$.next();
  }

  confirmSelection() {
    this.confirmSelectionClicked$.next();
  }

  onShouldPrintBarcodesApplyChange() {
    this.shouldPrintBarcodesApplyChanged$.next(this.shouldPrintBarcodesApply);
  }

  items: MenuItem[] = [
    {
      label: '<span class="text-l font-bold">Orders</span>',
      escape: false,
      expanded: true,
      items: [
        {
          label: 'Job Orders',
          icon: 'pi pi-fw pi-shopping-cart',
          routerLink: ['/in-process/manage/job-orders'],
        },
        {
          label: 'In-Process',
          icon: 'pi pi-fw pi-spinner',
          routerLink: ['/in-process/manage/jo-in-process'],
        },
        {
          label: 'Process Bins',
          icon: 'pi pi-fw pi-inbox',
          routerLink: ['/in-process/manage/addon-bins'],
        },
      ],
    },
    {
      label: '<span class="text-l font-bold">Shipping</span>',
      escape: false,
      expanded: true,
      items: [
        {
          label: 'Ship Prep',
          icon: 'pi pi-fw pi-qrcode',
          routerLink: ['/in-process/manage/scan-station'],
        },
        // {
        //   label: "Ship Prep",
        //   icon: "pi pi-fw pi-box",
        //   routerLink: ["/in-process/manage/jo-ship-prep"],
        // },
        {
          label: 'Staging / Vouchering',
          icon: 'pi pi-fw pi-th-large',
          routerLink: ['/in-process/manage/ship-bins'],
        },
        {
          label: 'Shipping',
          icon: 'pi pi-fw pi-truck',
          routerLink: ['/in-process/manage/jo-ship'],
        },
      ],
    },
    {
      label: '<span class="text-l font-bold">Utilities</span>',
      escape: false,
      expanded: true,
      items: [
        {
          label: 'Integrity Audits',
          icon: 'pi pi-fw pi-search',
          routerLink: ['/in-process/manage/jo-audit'],
        },
      ],
    },
  ];
}
